import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DataList } from "@/common/DataList";
import { handlePeriod } from "@/shared/utils/handle-contacts-null";
import type { ActivitiesTabFormValues } from "@/entities/activities-tab-content/model";
import { CareerField } from "./CareersField";

type Props = {
  personId: string;
  isEditMode: boolean;
  handleEditMode: () => void;
  onSubmit: (data: ActivitiesTabFormValues) => void;
  disabled?: boolean;
};

export const CareersList = ({
  personId,
  isEditMode,
  handleEditMode,
  onSubmit,
  disabled,
}: Props) => {
  const { t } = useTranslation();

  const { control, getValues, trigger } = useFormContext<ActivitiesTabFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "careers",
  });

  const onBlur = async () => {
    const isValid = await trigger("careers");

    if (isValid) {
      const formData = getValues();
      onSubmit(formData);
    }
  };

  const onChange = () => {
    trigger("careers");
  };

  const addHandler = () => {
    if (!isEditMode) {
      handleEditMode();
    }

    setTimeout(() => {
      append({
        industry: { name: "" },
        company: "",
        personId: personId,
        id: undefined,
        careerEnd: "",
        careerStart: "",
        jobTitle: "",
        about: "",
      });
    }, 0);
  };

  const removeHandler = async (index: number) => {
    remove(index);

    const isValid = await trigger("careers");

    if (isValid) {
      const formData = getValues();
      onSubmit(formData);
    }
  };

  return (
    <DataList
      data={fields.map((item) => ({
        id: item?.id ?? "",
        label: item?.company ?? "",
        value: item?.jobTitle ?? "",
        subLabel: item?.industry?.name ?? "",
        about: item?.about ?? "",
        subValue: handlePeriod(item?.careerStart, item?.careerEnd),
      }))}
      onAddClick={addHandler}
      onTrashClick={removeHandler}
      isEdit={isEditMode}
      title={t("contactsPage.activities.career", { count: 2 })}
      editNode={(index) => <CareerField index={index} onBlur={onBlur} onChange={onChange} />}
      disabled={disabled}
      hint={isEditMode ? t("contactsPage.editCareerHint") : undefined}
    />
  );
};
